import { formatPercentageOnInputChange } from '@fruitful/fruitils';
import type { DeepPartial, MoneyMapRuleWithAccount } from '@fruitful/types';
import { keyBy } from 'lodash';
import { useFieldArray, type UseFormReturn } from 'react-hook-form';
import { ControlledInput, FootnoteRegular } from 'smoothie';
import { z } from 'zod';

import { FormModuleContainer } from './FormModuleContainer';

export const groupPercentageSplitFormSchema = z.object({
    percentageSplit: z
        .object({
            ruleId: z.string().min(1),
            percentage: z.string().min(1),
        })
        .array(),
});

export type GroupPercentageSplitFormSchema = z.infer<
    typeof groupPercentageSplitFormSchema
>;

interface GroupPercentageSplitFormModuleProps<TContext> {
    rules: Array<DeepPartial<MoneyMapRuleWithAccount>>;
    formFunctions: UseFormReturn<GroupPercentageSplitFormSchema, TContext>;
}

export const GroupPercentageSplitFormModule = <TContext,>({
    rules,
    formFunctions,
}: GroupPercentageSplitFormModuleProps<TContext>) => {
    const {
        register,
        control,
        formState: { errors },
    } = formFunctions;

    const { fields } = useFieldArray({
        control,
        name: 'percentageSplit',
    });

    const rulesById = keyBy(rules, 'id');

    return (
        <FormModuleContainer title="Percentage split">
            {fields.map((field, index) => (
                <ControlledInput
                    key={field.id}
                    {...register(`percentageSplit.${index}.percentage`)}
                    placeholder="%"
                    control={control}
                    register={register}
                    errors={errors}
                    label={rulesById[field.ruleId]?.description}
                    size="tiny"
                    fontWeight="regular"
                    formatValueOnChange={formatPercentageOnInputChange}
                />
            ))}
            {errors.percentageSplit && (
                <FootnoteRegular color="$red1000">
                    {errors.percentageSplit.message}
                </FootnoteRegular>
            )}
        </FormModuleContainer>
    );
};
